/**
 * SiteGlue communication-channel messaging — single source of truth.
 *
 * Use these helpers for AI Summary, homepage, platform/features metadata,
 * role landings, footer copy, and structured data. Do not hard-code separate
 * marketing channel lists across pages.
 *
 * Plan capability matrices (e.g. Voice/WebRTC in AI_EMPLOYEE_PLANS) may include
 * additional product channels; this module defines the customer-facing
 * marketing channel set and copy.
 */

export type SiteGlueMarketingChannelId =
  | "phone"
  | "website_chat"
  | "sms"
  | "email"
  | "video_calls"
  | "whatsapp"
  | "messenger";

export type SiteGlueMarketingChannel = {
  id: SiteGlueMarketingChannelId;
  /** Primary label for lists and badges. */
  label: string;
  /** Title used in description sections. */
  title: string;
  /** Concise capability definition. */
  description: string;
  /** Team Takeover™ behavior for this channel. */
  takeover: string;
  /** Short label for constrained layouts (e.g. "chat"). */
  compactLabel: string;
};

/**
 * Supported marketing communication channels (order matters).
 * Phone · Website Chat · SMS · Email · Video Calls · WhatsApp · Messenger
 */
export const SITEGLUE_MARKETING_CHANNELS: readonly SiteGlueMarketingChannel[] = [
  {
    id: "phone",
    label: "Phone",
    title: "Phone Calls",
    description:
      "A SiteGlue Agent can answer inbound calls, collect information, route callers, transfer calls, take messages, and support approved appointment workflows.",
    takeover: "Transfer or route the call to a team member.",
    compactLabel: "phone",
  },
  {
    id: "website_chat",
    label: "Website Chat",
    title: "Website Chat",
    description:
      "A SiteGlue Agent engages visitors, answers questions, captures leads, schedules appointments, and supports live Team Takeover™.",
    takeover: "A team member joins the live conversation.",
    compactLabel: "chat",
  },
  {
    id: "sms",
    label: "SMS",
    title: "SMS",
    description:
      "A SiteGlue Agent continues customer conversations by text. A team member can pause the AI and continue messaging manually.",
    takeover: "Pause or disable the AI and allow the team to continue texting.",
    compactLabel: "SMS",
  },
  {
    id: "email",
    label: "Email",
    title: "Email",
    description:
      "A SiteGlue Agent can respond to approved email workflows. A team member can disable the AI for a thread and reply directly.",
    takeover: "Disable the AI for the thread and allow a team member to reply.",
    compactLabel: "email",
  },
  {
    id: "video_calls",
    label: "Video Calls",
    title: "Video Calls",
    description:
      "A SiteGlue Agent may support video-based greetings, consultations, demonstrations, or connected video-meeting workflows where enabled.",
    takeover:
      "Escalate to a human-hosted consultation, demonstration, or video meeting when configured.",
    compactLabel: "video",
  },
  {
    id: "whatsapp",
    label: "WhatsApp",
    title: "WhatsApp",
    description:
      "A SiteGlue Agent communicates with mobile-first customers through WhatsApp. Team members can pause the AI and continue the conversation.",
    takeover: "Pause the AI and allow the team to continue the conversation.",
    compactLabel: "WhatsApp",
  },
  {
    id: "messenger",
    label: "Messenger",
    title: "Messenger",
    description:
      "A SiteGlue Agent engages customers through Facebook Messenger. Team members can pause the AI and continue manually.",
    takeover: "Pause the AI and allow the team to continue the conversation.",
    compactLabel: "Messenger",
  },
] as const;

export const SITEGLUE_CHANNEL_CAPABILITY_NOTE =
  "Capabilities may vary by plan, configuration, region, channel provider, and integration. Do not assume every channel capability is available on every plan.";

export const SITEGLUE_CHANNEL_IDENTITY_NOTE =
  "Team Takeover™ behavior is not identical across all channels. SiteGlue does not claim perfect cross-channel identity resolution unless that capability is implemented for a given account.";

/** Full product / footer / body copy. */
export const SITEGLUE_CHANNEL_MESSAGING_FULL =
  "SiteGlue Agent — answers customer conversations, captures leads, schedules appointments, and turns interactions into opportunities across phone, website chat, SMS, email, video calls, WhatsApp, and Messenger.";

/** Compact copy when space is limited. */
export const SITEGLUE_CHANNEL_MESSAGING_COMPACT =
  "SiteGlue Agent — engage customers across phone, chat, SMS, email, video, WhatsApp, and Messenger.";

/**
 * Recommended site description (~280 chars). Prefer for long-form / AI Summary.
 * Root metadata should use the short form.
 */
export const SITEGLUE_SEO_DESCRIPTION_LONG =
  "SiteGlue Agent connects people and personal AI agents to answers, useful actions, and the right teammates across phone, website chat, SMS, email, video calls, WhatsApp, and Messenger — always with a Human-in-the-Loop™.";

/**
 * Constrained meta description (~115 chars) for title/OG/Twitter defaults.
 */
export const SITEGLUE_SEO_DESCRIPTION_SHORT =
  "SiteGlue Agent for phone, chat, SMS, email, video, WhatsApp, and Messenger — built for humans with Human-in-the-Loop™.";

export function siteGlueMarketingChannelLabels(): string[] {
  return SITEGLUE_MARKETING_CHANNELS.map((c) => c.label);
}

export function siteGlueMarketingChannelTitles(): string[] {
  return SITEGLUE_MARKETING_CHANNELS.map((c) => c.title);
}

/** Oxford-comma list: "phone, website chat, SMS, email, video calls, WhatsApp, and Messenger" */
export function siteGlueChannelListPhrase(mode: "full" | "compact" = "full"): string {
  const labels =
    mode === "compact"
      ? SITEGLUE_MARKETING_CHANNELS.map((c) => c.compactLabel)
      : ["phone", "website chat", "SMS", "email", "video calls", "WhatsApp", "Messenger"];
  if (labels.length <= 1) return labels[0] ?? "";
  const head = labels.slice(0, -1).join(", ");
  return `${head}, and ${labels[labels.length - 1]}`;
}

export function siteGlueTakeoverByChannel(): { channel: string; behavior: string }[] {
  return SITEGLUE_MARKETING_CHANNELS.map((c) => ({
    channel: c.label,
    behavior: c.takeover,
  }));
}

export function getSiteGlueMarketingChannel(
  id: SiteGlueMarketingChannelId,
): SiteGlueMarketingChannel {
  const found = SITEGLUE_MARKETING_CHANNELS.find((c) => c.id === id);
  if (!found) throw new Error(`Unknown SiteGlue marketing channel: ${id}`);
  return found;
}
